import React from 'react';
import { useIntl } from 'react-intl';
import { Button, Spinner } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { useSignOutAndReturnTo } from '../../redux/slices/appInfo/useOnLogout';
import { useUserToken } from '../../redux/slices/appInfo/hooks';
import { workspaceMessages } from '../workspaces/workspaceMessages';
import type { TeamAcceptInviteState } from './teamAcceptInviteState';

interface TeamAcceptInviteActionsProps {
  state: TeamAcceptInviteState;
  workspaceName: string;
  /** c5: the invite workspace is the active one, so the button only goes to Home. */
  isWorkspaceActive: boolean;
  isJoining: boolean;
  isOpening: boolean;
  onDecline: () => void;
  onJoin: () => void;
  onOpenWorkspace: () => void;
}

/**
 * "Not now", then the primary action of each state (design workspaces c): "Join workspace" (c1, a
 * spinner while it runs: c7), "Sign in with another email" (c4), "Open {workspace}" or "Go to Home"
 * (c5), a disabled "Join workspace" (c6).
 */
const TeamAcceptInviteActionsComponent: React.FC<TeamAcceptInviteActionsProps> = ({
  state,
  workspaceName,
  isWorkspaceActive,
  isJoining,
  isOpening,
  onDecline,
  onJoin,
  onOpenWorkspace,
}) => {
  const intl = useIntl();
  // c4: sign out and come back here after the next sign-in. The invite token stays in session
  // storage (`teamInviteToken`), which signing out does not clear.
  const userToken = useUserToken();
  const signInAgain = useSignOutAndReturnTo('team/accept', userToken);
  const isBusy = isJoining || isOpening;

  const joinLabel = intl.formatMessage({ id: 'app.sml.team.accept.join', defaultMessage: 'Join workspace' });

  const primary = (() => {
    switch (state) {
      case 'mismatch':
        return (
          <Button color="primary" className="flex-fill" onClick={signInAgain}>
            {intl.formatMessage({
              id: 'app.sml.team.accept.signInAgain',
              defaultMessage: 'Sign in with another email',
            })}
          </Button>
        );
      case 'full':
        return (
          <Button color="secondary" className="flex-fill" disabled>
            {joinLabel}
          </Button>
        );
      case 'alreadyMember':
        return (
          <Button color="primary" className="flex-fill" disabled={isBusy} onClick={onOpenWorkspace}>
            {isOpening && <Spinner size="sm" className="me-2" aria-hidden="true" />}
            {isOpening && intl.formatMessage(workspaceMessages.opening)}
            {!isOpening &&
              isWorkspaceActive &&
              intl.formatMessage({ id: 'app.sml.team.accept.goToHome', defaultMessage: 'Go to Home' })}
            {!isOpening &&
              !isWorkspaceActive &&
              intl.formatMessage(
                { id: 'app.sml.team.accept.openWorkspace', defaultMessage: 'Open {workspace}' },
                { workspace: workspaceName }
              )}
          </Button>
        );
      case 'ready':
      default:
        return (
          <Button color="primary" className="flex-fill" disabled={isJoining} onClick={onJoin}>
            {isJoining && <Spinner size="sm" className="me-2" aria-hidden="true" />}
            {isJoining
              ? intl.formatMessage({ id: 'app.sml.team.accept.joining', defaultMessage: 'Joining…' })
              : joinLabel}
          </Button>
        );
    }
  })();

  return (
    <div className="d-flex gap-2">
      <Button color="secondary" outline className="flex-fill" disabled={isBusy} onClick={onDecline}>
        {intl.formatMessage({ id: 'app.sml.team.accept.notNow', defaultMessage: 'Not now' })}
      </Button>
      {primary}
    </div>
  );
};

export const TeamAcceptInviteActions = TypedMemo(TeamAcceptInviteActionsComponent);
